import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Trash2, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { Category } from '@/lib/types';
import { toast } from 'sonner';

type CatForm = { slug: string; name: string; icon: string; sort_order: string };
const EMPTY: CatForm = { slug: '', name: '', icon: '📦', sort_order: '0' };

export default function AdminCategories() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [form, setForm] = useState<CatForm>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.from('categories').select('*').order('sort_order');
    setCategories((data ?? []) as Category[]);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = (c: Category) => {
    setEditing(c);
    setForm({ slug: c.slug, name: c.name, icon: c.icon ?? '', sort_order: String(c.sort_order) });
    setModalOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true);
    const payload = { slug: form.slug.trim(), name: form.name.trim(), icon: form.icon.trim() || null, sort_order: parseInt(form.sort_order) || 0 };
    let error;
    if (editing) {
      ({ error } = await supabase.from('categories').update(payload).eq('id', editing.id));
    } else {
      ({ error } = await supabase.from('categories').insert(payload));
    }
    if (error) toast.error(error.message);
    else { toast.success(editing ? 'Kategoria zaktualizowana!' : 'Kategoria dodana!'); setModalOpen(false); load(); }
    setSaving(false);
  };

  const handleDelete = async (id: number, name: string) => {
    if (!confirm(`Usunąć kategorię "${name}"?`)) return;
    setDeleting(String(id));
    const { error } = await supabase.from('categories').delete().eq('id', id);
    if (error) toast.error(error.message);
    else { toast.success('Kategoria usunięta.'); load(); }
    setDeleting(null);
  };

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-foreground" style={{ fontFamily: 'Orbitron, sans-serif' }}>KATEGORIE</h1>
          <p className="text-muted-foreground mt-1" style={{ fontFamily: 'Rajdhani, sans-serif' }}>Zarządzaj kategoriami figurek</p>
        </div>
        <Button onClick={openAdd} className="gap-2 bg-primary hover:bg-primary/90"><Plus className="w-4 h-4" /> Dodaj kategorię</Button>
      </div>

      <div className="rounded-2xl border overflow-hidden" style={{ borderColor: 'oklch(0.25 0.045 280)' }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: 'oklch(0.14 0.028 280)', borderBottom: '1px solid oklch(0.25 0.045 280)' }}>
              {['Ikona', 'Slug', 'Nazwa', 'Kolejność', 'Akcje'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-bold tracking-widest text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{h.toUpperCase()}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(4)].map((_, i) => (
                <tr key={i} style={{ borderBottom: '1px solid oklch(0.22 0.04 280)' }}>
                  {[...Array(5)].map((_, j) => <td key={j} className="px-4 py-3"><div className="h-4 rounded animate-pulse" style={{ background: 'var(--muted)', width: '60%' }} /></td>)}
                </tr>
              ))
            ) : categories.map(cat => (
              <tr key={cat.id} style={{ borderBottom: '1px solid oklch(0.20 0.035 280)', background: 'oklch(0.16 0.03 280)' }}
                onMouseEnter={e => (e.currentTarget as HTMLTableRowElement).style.background = 'oklch(0.18 0.04 280)'}
                onMouseLeave={e => (e.currentTarget as HTMLTableRowElement).style.background = 'oklch(0.16 0.03 280)'}
              >
                <td className="px-4 py-3 text-xl">{cat.icon}</td>
                <td className="px-4 py-3 font-mono text-xs text-accent">{cat.slug}</td>
                <td className="px-4 py-3 font-semibold text-foreground" style={{ fontFamily: 'Rajdhani, sans-serif' }}>{cat.name}</td>
                <td className="px-4 py-3 text-muted-foreground font-mono text-xs">{cat.sort_order}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(cat)} className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => handleDelete(cat.id, cat.name)} disabled={deleting === String(cat.id)} className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                      {deleting === String(cat.id) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AnimatePresence>
        {modalOpen && (
          <>
            <motion.div key="bd" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50" style={{ background: 'oklch(0.05 0.01 280 / 0.85)' }} onClick={() => setModalOpen(false)} />
            <motion.div key="md" initial={{ opacity: 0, scale: 0.93, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.93, y: 16 }} transition={{ type: 'spring', stiffness: 320, damping: 30 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
              <div className="pointer-events-auto w-full max-w-md rounded-2xl border p-7 relative" style={{ background: 'oklch(0.14 0.028 280)', borderColor: 'oklch(0.28 0.05 280)' }}>
                <button onClick={() => setModalOpen(false)} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
                <h2 className="text-xl font-black mb-6" style={{ fontFamily: 'Orbitron, sans-serif' }}>{editing ? 'EDYTUJ KATEGORIĘ' : 'NOWA KATEGORIA'}</h2>
                <form onSubmit={handleSave} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs tracking-widest text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>SLUG *</Label>
                      <Input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') }))} placeholder="sci-fi" className="bg-card border-border/60 font-mono text-xs" required disabled={!!editing} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs tracking-widest text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>IKONA (emoji)</Label>
                      <Input value={form.icon} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))} placeholder="🚀" className="bg-card border-border/60 text-xl text-center" maxLength={4} />
                    </div>
                    <div className="col-span-2 space-y-1.5">
                      <Label className="text-xs tracking-widest text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>NAZWA *</Label>
                      <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Sci-Fi" className="bg-card border-border/60" required />
                    </div>
                    <div className="col-span-2 space-y-1.5">
                      <Label className="text-xs tracking-widest text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>KOLEJNOŚĆ SORTOWANIA</Label>
                      <Input type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))} className="bg-card border-border/60" />
                    </div>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={() => setModalOpen(false)} className="flex-1">Anuluj</Button>
                    <Button type="submit" disabled={saving} className="flex-1 bg-primary hover:bg-primary/90">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Zapisz' : 'Dodaj')}
                    </Button>
                  </div>
                </form>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
